/**
 * PushRulesList: "Your SKU rules", the list half of the Skip or Always 0 card.
 *
 * The rules arrive whole (capped at `PUSH_SKU_RULES_CAP`, sorted by SKU on the
 * server), so the filter, the rule counts, the product groups and the pager all
 * work on one array in memory (CLAUDE.md: filter, count and paginate at the
 * same layer).
 *
 * Grouped by product id (DESIGN.md 12.1.1; two products that share a name stay
 * two groups): one `<tbody>` per product, headed by
 * a `th scope="rowgroup"` with a tri-state tick box, the product's name, how
 * many SKUs it has here and what they are set to ("All Always 0", "All Skip",
 * "Mixed"). A client's copies of its 3PL's garments have no product, so they
 * group by base garment instead, named after the garment and marked "3PL
 * garment"; the tick box takes every colour and size of it. Pages hold whole
 * products up to 50 SKUs; a bigger product is split
 * and its header repeats. Columns: tick | SKU | Rule | Product variant | Type
 * (Type from `sm` up). Type is ONE value: Base garment, else POD or PAP. A rule
 * that does nothing any more (`isInertStatus`) says so in its Rule cell,
 * dimmed, with the reason on a second line; the note above the table removes
 * them all in one click. A retired SKU's rule still applies, so it is not
 * dimmed and its second line just says Retired; a pre-printed design's rule
 * partly applies (its own stock rows), so it is not dimmed either and its
 * lines point at the base garment.
 *
 * The selection pill acts on what the filters show: a selected row a filter
 * hides is never changed out of sight. Once a change lands, the pill goes and
 * focus moves to the list heading.
 */
import { memo, useMemo, useState } from 'react';
import type { KeyboardEvent, RefObject } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { PUSH_SKU_RULES_CAP } from '@threadheads/shared';
import { AlertTriangle, Ban, ClipboardList, ListChecks, ListFilter, Loader2, PauseCircle, ShieldAlert, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FloatingSelectionBar } from '@/components/layout/FloatingSelectionBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { cn } from '@/lib/utils';
import {
  filterRules,
  groupRules,
  inertRuleIds,
  isInertStatus,
  paginateGroups,
  variantLabel,
  type GroupRuleSummary,
  type PushRuleRow,
  type PushSkuRuleOrNone,
  type RuleFilter,
  type RuleGroup,
} from '../utils/push-sku-rules';
import { RuleBadge, RuleStatusLine, SharedGarmentChip, SkuKindLabel } from './PushRuleBadge';
import { ALWAYS_ZERO_BUTTON_CLASS, REMOVE_RULE_CLASS } from './push-rule-styles';

/** The cap note shows once the list is this full. */
const CAP_WARNING_AT = Math.floor(PUSH_SKU_RULES_CAP * 0.9);
const ROW_BUTTON_CLASS = 'h-8 gap-1.5 px-3 text-xs';
const HEADING_ID = 'push-rules-list-heading';
const COLUMN_COUNT = 5;

/** What the selection bar has in flight, for its spinner. `inert`: the note's Remove these. */
export type ListPending = PushSkuRuleOrNone | 'inert';

interface PushRulesListProps {
  rules: PushRuleRow[];
  state: 'loading' | 'error' | 'ready';
  retrying: boolean;
  onRetry: () => void;
  busy: boolean;
  pending: ListPending | null;
  /** Resolves true once the change landed (the selection then clears), false if cancelled or failed. */
  onChangeRules: (skuIds: string[], rule: PushSkuRuleOrNone) => Promise<boolean>;
  /** Removes rules that do nothing any more; no confirm, they change nothing. */
  onRemoveInert: (skuIds: string[]) => void;
  onPaste: () => void;
  headingRef: RefObject<HTMLHeadingElement>;
  /** The host form's save bar is showing: lift the selection pill above it. */
  raiseSelectionBar: boolean;
}

/** Enter in the filter must never submit the settings form around this card. */
function filterKeyDown(event: KeyboardEvent<HTMLInputElement>, clear: () => void) {
  if (event.key === 'Enter') event.preventDefault();
  if (event.key === 'Escape' && event.currentTarget.value !== '') {
    event.preventDefault();
    clear();
  }
}

/** A group's header name: the product's, "Untitled product", or "No product". */
function groupName(group: Pick<RuleGroup, 'productId' | 'productTitle'>, t: TFunction<'inventory'>): string {
  if (group.productTitle !== null) return group.productTitle;
  return group.productId !== null ? t('stockSync.pushRules.product.untitled') : t('stockSync.pushRules.list.noProduct');
}

const SUMMARY_KEY: Record<GroupRuleSummary, string> = {
  always_zero: 'stockSync.pushRules.list.groupAllAlwaysZero',
  skip: 'stockSync.pushRules.list.groupAllSkip',
  mixed: 'stockSync.pushRules.list.groupMixed',
};

export const PushRulesList = memo(function PushRulesList({
  rules,
  state,
  retrying,
  onRetry,
  busy,
  pending,
  onChangeRules,
  onRemoveInert,
  onPaste,
  headingRef,
  raiseSelectionBar,
}: PushRulesListProps) {
  const { t } = useTranslation('inventory');
  const [filterText, setFilterText] = useState('');
  const [ruleFilter, setRuleFilter] = useState<RuleFilter>('all');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());

  const inertIds = useMemo(() => inertRuleIds(rules), [rules]);
  const view = useMemo(() => filterRules(rules, filterText, ruleFilter), [rules, filterText, ruleFilter]);
  const groups = useMemo(() => groupRules(view.rows), [view.rows]);
  const pageView = paginateGroups(groups, page);
  // The bar acts on what the filters show: a selected row a filter hides is
  // never changed out of sight, and a row that left the list drops out.
  const visibleIds = useMemo(() => new Set(view.rows.map((row) => row.skuId)), [view.rows]);
  const selectedIds = useMemo(() => [...selected].filter((id) => visibleIds.has(id)), [selected, visibleIds]);
  const pageIds = pageView.slices.flatMap((slice) => slice.rows.map((row) => row.skuId));

  const triState = (ids: readonly string[]): boolean | 'indeterminate' => {
    const on = ids.filter((id) => selected.has(id)).length;
    if (ids.length > 0 && on === ids.length) return true;
    return on > 0 ? 'indeterminate' : false;
  };
  const setMany = (ids: readonly string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  const setFilter = (next: RuleFilter) => {
    setRuleFilter(next);
    setPage(0);
  };
  const setFilterQuery = (next: string) => {
    setFilterText(next);
    setPage(0);
  };
  const clearFilters = () => {
    setFilterText('');
    setRuleFilter('all');
    setPage(0);
  };

  const run = async (rule: PushSkuRuleOrNone) => {
    if (selectedIds.length === 0) return;
    const ok = await onChangeRules(selectedIds, rule);
    if (ok) setSelected(new Set());
  };

  const heading = (
    <h4
      ref={headingRef}
      id={HEADING_ID}
      tabIndex={-1}
      className="rounded-sm text-sm font-medium outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
    >
      {t('stockSync.pushRules.list.heading', { total: rules.length.toLocaleString() })}
    </h4>
  );

  let body;
  if (state === 'error') {
    body = (
      <SectionError
        variant="compact"
        title={t('stockSync.pushRules.list.errorTitle')}
        body={t('stockSync.pushRules.list.errorBody')}
        retryLabel={t('stockSync.pushRules.retry')}
        retrying={retrying}
        onRetry={onRetry}
      />
    );
  } else if (state === 'loading') {
    body = (
      <div className="space-y-1.5" role="status" aria-busy="true">
        <span className="sr-only">{t('stockSync.pushRules.list.loading')}</span>
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  } else if (rules.length === 0) {
    body = (
      <EmptyState
        size="sm"
        icon={ListChecks}
        title={t('stockSync.pushRules.list.emptyTitle')}
        description={t('stockSync.pushRules.list.emptyBody')}
        className="rounded-md border border-dashed"
        action={
          <Button type="button" variant="outline" className={ROW_BUTTON_CLASS} onClick={onPaste}>
            <ClipboardList className="h-3.5 w-3.5" aria-hidden="true" />
            {t('stockSync.pushRules.pasteList')}
          </Button>
        }
      />
    );
  } else {
    body = (
      <>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 max-w-prose flex-1 basis-56">
            <ListFilter
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              value={filterText}
              onChange={(event) => setFilterQuery(event.target.value)}
              onKeyDown={(event) => filterKeyDown(event, () => setFilterQuery(''))}
              aria-label={t('stockSync.pushRules.list.filterLabel')}
              placeholder={t('stockSync.pushRules.list.filterPlaceholder')}
              autoComplete="off"
              spellCheck={false}
              className="h-8 pl-8 text-sm"
            />
          </div>
          <SegmentedControl<RuleFilter>
            ariaLabel={t('stockSync.pushRules.list.ruleFilterLabel')}
            value={ruleFilter}
            onChange={setFilter}
            options={[
              { value: 'all', label: t('stockSync.pushRules.list.all'), badge: view.counts.all },
              { value: 'skip', label: t('stockSync.pushRules.rule.skip'), badge: view.counts.skip },
              { value: 'always_zero', label: t('stockSync.pushRules.rule.alwaysZero'), badge: view.counts.alwaysZero },
            ]}
          />
        </div>

        {inertIds.length > 0 && (
          <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
            <NoteLine tone="warning" icon={AlertTriangle} className="min-w-0 max-w-prose">
              {t('stockSync.pushRules.list.inertNote', { count: inertIds.length })}
            </NoteLine>
            <Button
              type="button"
              variant="link"
              className="h-auto gap-1 p-0 text-xs"
              disabled={busy}
              aria-busy={pending === 'inert' || undefined}
              onClick={() => onRemoveInert(inertIds)}
            >
              {pending === 'inert' && (
                <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              )}
              {t('stockSync.pushRules.list.removeInert', { count: inertIds.length })}
            </Button>
          </div>
        )}

        {rules.length >= CAP_WARNING_AT && (
          <NoteLine tone="warning" icon={ShieldAlert}>
            {t('stockSync.pushRules.list.nearCap', {
              used: rules.length.toLocaleString(),
              cap: PUSH_SKU_RULES_CAP.toLocaleString(),
            })}
          </NoteLine>
        )}

        {view.rows.length === 0 ? (
          <div className="rounded-md border border-dashed px-4 py-6 text-center text-sm">
            <p className="text-muted-foreground">
              {filterText.trim()
                ? t('stockSync.pushRules.list.noMatchQuery', { q: filterText.trim() })
                : t('stockSync.pushRules.list.noMatch')}
            </p>
            <Button type="button" variant="link" className="h-auto p-0 text-sm" onClick={clearFilters}>
              {t('stockSync.pushRules.list.clearFilters')}
            </Button>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table aria-labelledby={HEADING_ID}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="h-9 w-10 px-3">
                    <Checkbox
                      checked={triState(pageIds)}
                      onCheckedChange={(value) => setMany(pageIds, value === true)}
                      aria-label={t('stockSync.pushRules.list.selectPage')}
                    />
                  </TableHead>
                  <TableHead className="h-9 px-3">{t('stockSync.pushRules.list.colSku')}</TableHead>
                  <TableHead className="h-9 px-3">{t('stockSync.pushRules.list.colRule')}</TableHead>
                  <TableHead className="h-9 px-3">{t('stockSync.pushRules.list.colVariant')}</TableHead>
                  <TableHead className="hidden h-9 px-3 sm:table-cell">{t('stockSync.pushRules.list.colType')}</TableHead>
                </TableRow>
              </TableHeader>
              {pageView.slices.map((slice, index) => {
                const { group } = slice;
                const groupIds = group.rows.map((row) => row.skuId);
                const name = groupName(group, t);
                return (
                  <TableBody key={`${group.key}:${slice.continued ? 'cont' : 'start'}`}>
                    <TableRow className={cn('bg-muted/30 hover:bg-muted/30', index > 0 && 'border-t')}>
                      <th scope="rowgroup" colSpan={COLUMN_COUNT} className="px-3 py-2 text-left font-normal">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <Checkbox
                            checked={triState(groupIds)}
                            onCheckedChange={(value) => setMany(groupIds, value === true)}
                            aria-label={t('stockSync.pushRules.list.selectGroup', { product: name })}
                          />
                          <span className="min-w-0 max-w-[24rem] truncate text-sm font-medium" title={name}>
                            {name}
                            {slice.continued && (
                              <span className="font-normal text-muted-foreground">
                                {' '}
                                {t('stockSync.pushRules.list.continued')}
                              </span>
                            )}
                          </span>
                          {group.garmentCode !== null && <SharedGarmentChip />}
                          <span className="text-xs tabular-nums text-muted-foreground">
                            {t('stockSync.pushRules.list.groupSkus', { count: group.rows.length })}
                          </span>
                          <span className="ml-auto text-xs text-muted-foreground">{t(SUMMARY_KEY[group.summary])}</span>
                        </div>
                      </th>
                    </TableRow>
                    {slice.rows.map((row) => {
                      const isSelected = selected.has(row.skuId);
                      const inert = isInertStatus(row.status);
                      const variant = variantLabel(row.title, group.productTitle);
                      return (
                        <TableRow
                          key={row.skuId}
                          data-state={isSelected ? 'selected' : undefined}
                          className="hover:bg-muted/40"
                        >
                          <TableCell className="w-10 px-3 py-1.5">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(value) => setMany([row.skuId], value === true)}
                              aria-label={
                                row.sku
                                  ? t('stockSync.pushRules.list.selectRow', { sku: row.sku })
                                  : t('stockSync.pushRules.list.selectRowNoCode', {
                                      // Same words as the row's status line.
                                      status: t(`stockSync.pushRules.status.${row.status === 'ok' ? 'not_found' : row.status}`),
                                    })
                              }
                            />
                          </TableCell>
                          <TableCell className="px-3 py-1.5">
                            {row.sku ? (
                              <span className="break-all font-mono text-xs">{row.sku}</span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="px-3 py-1.5">
                            <div className="flex flex-col items-start gap-0.5">
                              <RuleBadge rule={row.rule} dimmed={inert} />
                              {/* A rule the push no longer reaches says why, and stays removable. */}
                              <RuleStatusLine status={!row.sku && row.status === 'ok' ? 'not_found' : row.status} />
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[14rem] px-3 py-1.5">
                            {variant ? (
                              <p className="truncate text-sm" title={variant}>
                                {variant}
                              </p>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="hidden px-3 py-1.5 sm:table-cell">
                            <SkuKindLabel productionType={row.productionType} isBaseGarment={row.isBaseGarment} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                );
              })}
            </Table>
          </div>
        )}

        {view.rows.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="tabular-nums">
              {t('stockSync.pushRules.list.range', {
                from: pageView.from,
                to: pageView.to,
                total: pageView.total,
              })}
            </span>
            {pageView.pageCount > 1 && (
              <div className="ml-auto flex gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  className={ROW_BUTTON_CLASS}
                  disabled={pageView.page === 0}
                  onClick={() => setPage(pageView.page - 1)}
                >
                  {t('stockSync.pushRules.list.previous')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className={ROW_BUTTON_CLASS}
                  disabled={pageView.page >= pageView.pageCount - 1}
                  onClick={() => setPage(pageView.page + 1)}
                >
                  {t('stockSync.pushRules.list.next')}
                </Button>
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <section aria-labelledby={HEADING_ID} className="space-y-3">
      {heading}
      {body}

      <FloatingSelectionBar
        visible={selectedIds.length > 0}
        srStatus={t('stockSync.pushRules.selection.count', { count: selectedIds.length })}
        ariaLabel={t('stockSync.pushRules.selection.label')}
        skin="wrap"
        // Lift above the host form's "Unsaved changes" bar (sticky bottom-4)
        // while it shows, so neither hides the other.
        className={cn(raiseSelectionBar && 'bottom-24')}
      >
        <span className="whitespace-nowrap text-sm font-medium tabular-nums">
          {t('stockSync.pushRules.selection.count', { count: selectedIds.length })}
        </span>
        <div className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden="true" />
        {selectedIds.length < view.rows.length && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => setSelected(new Set(view.rows.map((row) => row.skuId)))}
          >
            {t('stockSync.pushRules.selection.selectAll', { count: view.rows.length })}
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={busy}
          // An X alone below sm (the pill wraps there); the name always says what it clears.
          aria-label={t('stockSync.pushRules.selection.clearLabel')}
          onClick={() => setSelected(new Set())}
        >
          <X className="h-3.5 w-3.5 sm:hidden" aria-hidden="true" />
          <span className="hidden sm:inline">{t('stockSync.pushRules.selection.clear')}</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={busy}
          aria-busy={pending === 'skip' || undefined}
          onClick={() => void run('skip')}
        >
          {pending === 'skip' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : (
            <PauseCircle className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {t('stockSync.pushRules.selection.setSkip')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn('gap-1.5', ALWAYS_ZERO_BUTTON_CLASS)}
          disabled={busy}
          onClick={() => void run('always_zero')}
        >
          <Ban className="h-3.5 w-3.5" aria-hidden="true" />
          {t('stockSync.pushRules.selection.setAlwaysZero')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn('gap-1.5', REMOVE_RULE_CLASS)}
          disabled={busy}
          aria-busy={pending === 'none' || undefined}
          onClick={() => void run('none')}
        >
          {pending === 'none' && (
            <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          )}
          {t('stockSync.pushRules.selection.remove')}
        </Button>
      </FloatingSelectionBar>
    </section>
  );
});
